import React from "react";

import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  PieChart,
  Pie,
  Cell,
  Tooltip,
  Legend,
  ResponsiveContainer,
  ComposedChart,
  Line,
  LineChart,
  LabelList,
} from "recharts";

//Donut chart with needle
export const Vehiclechart = ({ value }) => {
  const data = [{ name: "A", value: 100 }];
  const total = data.reduce((acc, v) => acc + v.value, 10);
  const percentage = (value / total) * 100; // Normalize value to 100%

  const COLORS = ["#76FF7F", "#C4CAFF"];
  const RADIAN = Math.PI / 180;
  const cx = 200;
  const cy = 200;
  const iR = 50;
  const oR = 80;

  const needle = (percentage, cx, cy, iR, oR, color) => {
    const angle = 180 * (1 - percentage / 100); // Convert percentage to angle
    const length = (iR + 2 * oR) / 3;
    const sin = Math.sin(-RADIAN * angle);
    const cos = Math.cos(-RADIAN * angle);
    const r = 5;
    const x0 = cx;
    const y0 = cy;
    const xba = x0 + r * sin;
    const yba = y0 - r * cos;
    const xbb = x0 - r * sin;
    const ybb = y0 + r * cos;
    const xp = x0 + length * cos;
    const yp = y0 + length * sin;

    return [
      <circle key="circle" cx={x0} cy={y0} r={r} fill={color} stroke="none" />,
      <path
        key="needle"
        d={`M${xba} ${yba}L${xbb} ${ybb} L${xp} ${yp} L${xba} ${yba}`}
        stroke="none"
        fill={color}
      />,
    ];
  };

  return (
    <div>
      <PieChart width={400} height={300}>
        <Pie
          data={data}
          cx={cx}
          cy={cy}
          startAngle={180}
          endAngle={0}
          innerRadius={70}
          outerRadius={120}
          fill="#8884d8"
          dataKey="value"
        >
          {data.map((entry, index) => (
            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
          ))}
        </Pie>
        {needle(percentage, cx, cy, iR, oR, "#1F243B")}
      </PieChart>
    </div>
  );
};

//Bar chart for consignment

export const TotalConsignment = ({ data, width, height }) => {
  return (
    <ResponsiveContainer width={width} height={height}>
      {data?.length > 0 ? (
        <BarChart
          margin={{ top: 20, right: 30, left: 20, bottom: 10 }}
          barSize={28}
          data={data}
          barCategoryGap={5}
          barGap={1}
          style={{ cursor: "pointer" }}
        >
          <CartesianGrid vertical={false} horizontal={true} />
          <XAxis
            dataKey="Data"
            axisLine={false}
            tickLine={false}
            tick={({ x, y, payload }) => {
              const words = payload.value.split(" ");
              return (
                <text
                  x={x}
                  y={y + 10}
                  textAnchor="middle"
                  fill="#666"
                  fontSize={14}
                >
                  {words.map((word, index) => (
                    <tspan key={index} x={x} dy={index === 0 ? 0 : 14}>
                      {word}
                    </tspan>
                  ))}
                </text>
              );
            }}
          />
          <YAxis dataKey="Val" axisLine={false} tickLine={false} />
          <Tooltip />
          {/* <Legend /> */}
          <Bar dataKey="Val" fill="#6DB6FF" radius={[5, 5, 0, 0]}>
            <LabelList
              content={({ x, y, width, value }) => (
                <text
                  // x={x + width + 5} // 5px padding to the right of the bar
                  // y={y + 14} // vertically center the label in the bar
                  y={y + width - 38}
                  x={x}
                  fill="#000"
                  fontSize={12}
                >
                  {value}
                </text>
              )}
            />
          </Bar>
        </BarChart>
      ) : (
        <center>No Data</center>
      )}
    </ResponsiveContainer>
  );
};

//Vehicle's Performance by Trip
export const VerticalChart = ({
  data,
  width,
  height,
  barColor,
  selectedOption,
  // dataKey,
}) => {
  const getYAxisDataKey = () => {
    switch (selectedOption) {
      case "Driver":
        return "Driver_Name";
      case "Vehicle":
      default:
        return "Vehicle_No";
    }
  };

  // Determine the label text based on selectedOption
  const getYAxisLabel = () => {
    switch (selectedOption) {
      case "Driver":
        return "Driver";
      case "Vehicle":
      default:
        return "Vehicle";
    }
  };

  // Dynamically calculate left margin based on longest label in data
  const getLeftMargin = () => {
    if (!data?.length) return 100;
    const key = getYAxisDataKey();
    const maxLength = Math.max(...data.map((d) => (d[key] || "").length));
    return Math.min(maxLength * 7, 200); // 7px per char, max 200px
  };

  const yAxisKey = getYAxisDataKey();
  const yAxisLabel = getYAxisLabel();
  return (
    <ResponsiveContainer width={width} height={height}>
      {data?.length > 0 ? (
        <BarChart
          layout="vertical"
          margin={{ top: 20, right: 30, left: 100, bottom: 10 }}
          barSize={28}
          data={data}
          style={{ cursor: "pointer" }}
        >
          <CartesianGrid vertical={false} horizontal={false} />
          <XAxis type="number" axisLine={false} tickLine={false} />
          <YAxis
            type="category"
            dataKey={yAxisKey}
            axisLine={false}
            width={getLeftMargin()}
            tickLine={false}
            label={{
              value: yAxisLabel,
              position: "insideBottom",
              offset: -17,
              dx: -50, //adjust this value to move further left
              style: { fill: "black", fontSize: 14 },
            }}
          />
          <Tooltip />
          {/* <Legend /> */}
          <Bar dataKey="value" fill={barColor} radius={5}>
            <LabelList
              content={({ x, y, width, value }) => (
                <text
                  x={x + width + 5} // 5px padding to the right of the bar
                  y={y + 14} // vertically center the label in the bar
                  fill="#000"
                  fontSize={12}
                >
                  {value}
                </text>
              )}
            />
          </Bar>
        </BarChart>
      ) : (
        <center>No Data</center>
      )}
    </ResponsiveContainer>
  );
};

//vehicle performance data (vehicle Report)
export const VRvehiclePerformance = ({ data, width, height }) => {
  return (
    <ResponsiveContainer width={width} height={height}>
      {data?.length > 0 ? (
        <ComposedChart
          data={data}
          margin={{
            top: 40,
            right: 30,
            left: 20,
            bottom: 5,
          }}
          barCategoryGap="25%"
          barGap={0}
          style={{ cursor: "pointer" }}
        >
          <Legend
            layout="horizontal"
            align="center"
            verticalAlign="top"
            wrapperStyle={{
              position: "absolute",
              top: 0,
              left: "50%",
              transform: "translateX(-50%)",
              borderRadius: "5px",
            }}
          />
          <CartesianGrid vertical={false} horizontal={true} />
          <XAxis dataKey="Data" axisLine={false} tickLine={false} />
          <YAxis
            axisLine={false}
            tickLine={false}
            domain={[0, "dataMax"]} // Ensures the Y-axis starts at 0 and ends at the maximum value in the data
            tickCount={200} // Specifies the number of ticks (adjust as needed)
            tickFormatter={(value) => value.toLocaleString()} // Formats large numbers with commas
          />
          <Tooltip />
          <Bar
            dataKey="Fuel"
            name="Fuel (L)"
            fill="#FFD48F"
            radius={[5, 5, 0, 0]}
          >
            <LabelList
              content={({ x, y, width, value }) => (
                <text
                  y={y + width - 38}
                  x={x} // vertically center the label in the bar
                  fill="#000"
                  fontSize={12}
                >
                  {value}
                </text>
              )}
            />
          </Bar>
          <Bar
            dataKey="Distance"
            name="Distance (Km)"
            fill="#0EDB86"
            radius={[5, 5, 0, 0]}
          >
            <LabelList
              content={({ x, y, width, value }) => (
                <text
                  y={y + width - 38}
                  x={x} // vertically center the label in the bar
                  fill="#000"
                  fontSize={12}
                >
                  {value}
                </text>
              )}
            />
          </Bar>
          <Line
            type="basis"
            dataKey="Mileage"
            name="Mileage (Km/Hr)"
            stroke="#004CE4"
            dot={false}
          />
        </ComposedChart>
      ) : (
        <center>No Data</center>
      )}
    </ResponsiveContainer>
  );
};

// vehicle performance by mileage (Km/L)
export const VehicleMileage = ({ data }) => {
  const sortedData = React.useMemo(() => {
    if (!data) return [];

    return [...data]
      .map((item) => ({
        ...item,
        Mileage: Number(item.Mileage), // convert to number
      }))
      .sort((a, b) => a.Mileage - b.Mileage); // ascending
  }, [data]);

  return (
    <div style={{ width: "100%" }}>
      <ResponsiveContainer width="100%" height={500}>
        {sortedData?.length > 0 ? (
          <LineChart
            data={sortedData}
            margin={{ top: 20, right: 40, bottom: 40, left: 0 }}
          >
            <CartesianGrid vertical={false} horizontal={true} />
            <XAxis
              dataKey="REGNO"
              tick={{
                dy: 10,
                textAnchor: "middle",
              }}
            />
            <YAxis
              axisLine={false}
              tickLine={false}
              domain={[0, "dataMax"]} // Ensures the Y-axis starts at 0 and ends at the maximum value in the data
              tickCount={200} // Specifies the number of ticks (adjust as needed)
              tickFormatter={(value) => value.toLocaleString()} // Formats large numbers with commas
            />
            <Tooltip formatter={(value) => Number(value).toFixed(2)} />
            <Line
              type="linear"
              dataKey="Mileage"
              stroke="#FF7171"
              strokeWidth={2}
              dot={{
                r: 6,
                fill: "#FF7171",
                strokeWidth: 2,
              }}
            >
              <LabelList
                dataKey="Mileage"
                content={({ x, y, value }) => {
                  const formatted = Number(value).toFixed(2); // Format to 2 decimals
                  return (
                    <text
                      x={x}
                      y={y - 10} // 10px above the point
                      fill="#FF7171"
                      fontSize={12}
                      fontWeight="bold"
                      textAnchor="middle"
                    >
                      {formatted}
                    </text>
                  );
                }}
              />
            </Line>
          </LineChart>
        ) : (
          <center>No Data</center>
        )}
      </ResponsiveContainer>
    </div>
  );
};
